import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Mail, Search, UserPlus, Users, X } from "lucide-react";
import { useConnections, useCreateBookingInvite } from "@/lib/api";
import { useToast } from "@/components/ux/Toast";
import tracking from "@/lib/tracking";
import { UserAPI } from "@shared/types/api";
import { getEventTypeNoun } from "@/utils/eventTypeUtils";
import { useT } from "@/lib/i18n";

interface Props {
  isOpen: boolean;
  bookingId: string;
  /** Event type for the booking — drives copy ("…to dinner" / "…to brunch"). */
  eventType?: string | null;
  /** User IDs already invited to this booking — used to disable rows for
   *  connections we've already reached out to. */
  alreadyInvitedUserIds: string[];
  alreadyInvitedEmails: string[];
  onClose: () => void;
  onSent: () => void;
}

type Tab = "connections" | "email";

/**
 * Two-tab invite composer:
 *   - "Your connections" — picks from prior dinner connections (people the user
 *     has actually met) and fires an in-app invite via inviteeUserId.
 *   - "By email" — fallback for anyone not yet in the system.
 *
 * Both paths funnel through `useCreateBookingInvite` so server-side bookkeeping
 * (tracking, email send, share URL generation) is identical.
 */
export function InviteFriendsModal({
  isOpen,
  bookingId,
  eventType,
  alreadyInvitedUserIds,
  alreadyInvitedEmails,
  onClose,
  onSent,
}: Props) {
  const { t } = useT();
  const eventNoun = getEventTypeNoun(eventType);
  const toast = useToast();
  const { data: connectionsData, isLoading: connectionsLoading } =
    useConnections();
  const create = useCreateBookingInvite(bookingId);

  // Email is the default — for most invites the inviter is reaching outside
  // their existing Outclose network. The Connections tab only shows up when
  // there are actual previous-dinner connections to pick from.
  const [tab, setTab] = useState<Tab>("email");
  const [search, setSearch] = useState("");
  const [email, setEmail] = useState("");
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);

  const invitedUserSet = useMemo(
    () => new Set(alreadyInvitedUserIds),
    [alreadyInvitedUserIds],
  );
  const invitedEmailSet = useMemo(
    () => new Set(alreadyInvitedEmails.map((e) => e.toLowerCase())),
    [alreadyInvitedEmails],
  );

  const connections = useMemo(
    () => (connectionsData?.data || []) as UserAPI.UserConnection[],
    [connectionsData?.data],
  );
  const hasConnections = connections.length > 0;
  const filteredConnections = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return connections;
    return connections.filter((c) => {
      const name = `${c.connectedUser.firstName} ${c.connectedUser.lastName || ""}`
        .toLowerCase();
      return name.includes(q);
    });
  }, [connections, search]);

  if (!isOpen) return null;

  const handleInviteConnection = async (userId: string, name: string) => {
    setPendingUserId(userId);
    tracking.inviteSendClicked(bookingId);
    try {
      const res = await create.mutateAsync({ inviteeUserId: userId });
      if (res.success) {
        toast.success(t("invites.modal.inviteSentTo", { recipient: name }));
        onSent();
        onClose();
      } else {
        toast.error(res.error || t("invites.modal.failedToSend"));
      }
    } catch (e: any) {
      toast.error(e?.message || t("invites.modal.failedToSend"));
    } finally {
      setPendingUserId(null);
    }
  };

  const handleInviteEmail = async () => {
    const trimmed = email.trim();
    if (!trimmed) return;
    if (invitedEmailSet.has(trimmed.toLowerCase())) {
      toast.error(t("invites.modal.alreadyInvited"));
      return;
    }
    tracking.inviteSendClicked(bookingId);
    try {
      const res = await create.mutateAsync({ email: trimmed });
      if (res.success && res.data) {
        toast.success(t("invites.modal.inviteSentTo", { recipient: trimmed }));
        if (res.data.shareUrl) {
          try {
            await navigator.clipboard.writeText(res.data.shareUrl);
            tracking.inviteLinkCopied(res.data.id, "create");
          } catch {
            // best-effort
          }
        }
        setEmail("");
        onSent();
        onClose();
      } else {
        toast.error(res.error || t("invites.modal.failedToSend"));
      }
    } catch (e: any) {
      toast.error(e?.message || t("invites.modal.failedToSend"));
    }
  };

  return (
    <div
      className="modal modal-open z-[9999]"
      // The modal is rendered inside ReservationCard, which has its own
      // onClick that navigates to /booking/:id. Without stopPropagation
      // on the backdrop, clicking outside the modal-box would close the
      // modal AND trigger that navigation. Same for the Esc-equivalent
      // path: the close button below also needs propagation stopped.
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        transition={{ duration: 0.15 }}
        className="modal-box relative card max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary-500/15 flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-primary-500" />
            </div>
            <div>
              <h3 className="font-bold text-lg leading-tight">
                {t("invites.modal.title")}
              </h3>
              <p className="text-xs text-dark-muted">
                {t("invites.modal.subtitle", { eventNoun })}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded text-base-content/60 hover:bg-base-content/10"
            aria-label={t("invites.modal.close")}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tabs — only rendered when there's actually a Connections option to
            pick from. Otherwise the email form takes the whole modal. */}
        {hasConnections && (
          <div className="flex gap-1 p-1 rounded-lg bg-base-200/60 mb-4">
            <button
              onClick={() => setTab("email")}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                tab === "email"
                  ? "bg-base-100 shadow-sm"
                  : "text-dark-muted hover:text-base-content"
              }`}
            >
              <Mail className="w-4 h-4" />
              {t("invites.modal.tabs.byEmail")}
            </button>
            <button
              onClick={() => setTab("connections")}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                tab === "connections"
                  ? "bg-base-100 shadow-sm"
                  : "text-dark-muted hover:text-base-content"
              }`}
            >
              <Users className="w-4 h-4" />
              {t("invites.modal.tabs.connections")}
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-primary-500/15 text-primary-500">
                {connections.length}
              </span>
            </button>
          </div>
        )}

        {/* Body */}
        {hasConnections && tab === "connections" ? (
          <div>
            {connectionsLoading ? (
              <div className="flex items-center justify-center py-8 text-dark-muted">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            ) : (
              <>
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-muted" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t("invites.modal.searchByName")}
                    className="input input-bordered input-sm w-full pl-9"
                  />
                </div>
                <div className="max-h-72 overflow-y-auto space-y-1 -mx-1 px-1">
                  {filteredConnections.length === 0 && (
                    <div className="text-sm text-dark-muted text-center py-6">
                      {t("invites.modal.noMatches")}
                    </div>
                  )}
                  {filteredConnections.map((c) => {
                    const user = c.connectedUser;
                    const name = `${user.firstName}${user.lastName ? " " + user.lastName : ""}`;
                    const alreadyInvited = invitedUserSet.has(user.id);
                    const isPending = pendingUserId === user.id;
                    return (
                      <div
                        key={c.id}
                        className="flex items-center justify-between gap-2 px-2 py-2 rounded hover:bg-base-200/40"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 rounded-full bg-primary-500/15 flex items-center justify-center text-xs font-semibold text-primary-500 shrink-0">
                            {user.firstName.charAt(0).toUpperCase()}
                            {user.lastName?.charAt(0).toUpperCase() || ""}
                          </div>
                          <div className="text-sm truncate">{name}</div>
                        </div>
                        <button
                          onClick={() =>
                            handleInviteConnection(user.id, name)
                          }
                          disabled={alreadyInvited || isPending}
                          className="btn btn-primary btn-xs"
                        >
                          {isPending ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : alreadyInvited ? (
                            t("invites.modal.invited")
                          ) : (
                            t("invites.modal.invite")
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-dark-muted block mb-1.5">
                {t("invites.modal.emailLabel")}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("invites.modal.emailPlaceholder")}
                className="input input-bordered w-full"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && email.trim() && !create.isPending) {
                    handleInviteEmail();
                  }
                }}
              />
              <p className="text-xs text-dark-muted mt-2">
                {t("invites.modal.emailHelp")}
              </p>
            </div>
            <button
              onClick={handleInviteEmail}
              disabled={create.isPending || !email.trim()}
              className="btn btn-primary w-full"
            >
              {create.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("invites.modal.sending")}
                </>
              ) : (
                t("invites.modal.sendInvite")
              )}
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
